import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, Store, Truck, Calendar, MapPin } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { API_BASE_URL } from '../shared/utils/apiConfig';

// Pick the Square environment by host: local/dev stays on Sandbox; the live site
// (friendamals.com, nicherest.com, etc.) uses Production. One static build serves
// all domains, so this has to be decided at runtime, not build time.
function detectCheckoutEnv(): 'sandbox' | 'production' {
  if (typeof window === 'undefined') return 'sandbox';
  const h = window.location.hostname.toLowerCase();
  const isLocal =
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h.endsWith('.local') ||
    h.startsWith('192.168.') ||
    h.startsWith('10.') ||
    h.startsWith('172.16.');
  return isLocal ? 'sandbox' : 'production';
}
const CHECKOUT_ENV = detectCheckoutEnv();

interface PickupEvent {
  id: string;
  name: string;
  date: string; // yyyy-mm-dd (normalized)
  city?: string;
  state?: string;
  venue?: string;
}

interface RateOption {
  rateId: string;
  provider: string;
  service: string;
  amount: number;
  currency: string;
  estimatedDays: number | null;
}

function loadSquareSdk(sandbox: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (w.Square) return resolve(w.Square);
    const src = sandbox
      ? 'https://sandbox.web.squarecdn.com/v1/square.js'
      : 'https://web.squarecdn.com/v1/square.js';
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve((window as any).Square);
    s.onerror = () => reject(new Error('Failed to load Square'));
    document.head.appendChild(s);
  });
}

function fmtEventDate(d: string): string {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  border: '1px solid #ddd',
  borderRadius: '6px',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

export function CheckoutPage() {
  const { cart, clearCart } = useCart();
  const DEV_PREFILL = CHECKOUT_ENV === 'sandbox';
  const [firstName, setFirstName] = useState(DEV_PREFILL ? 'Lory' : '');
  const [lastName, setLastName] = useState(DEV_PREFILL ? 'Livezey' : '');
  const [email, setEmail] = useState(DEV_PREFILL ? 'lory@incuvation.com' : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [cardReady, setCardReady] = useState(false);
  const [done, setDone] = useState(false);
  // Remember the vendor after the cart is cleared so the confirmation screen can
  // route "Continue shopping" back to the shop (not nicherest home).
  const [placedSlug, setPlacedSlug] = useState('');
  const [placedVendorName, setPlacedVendorName] = useState('');
  const cardRef = useRef<any>(null);

  // Fulfillment
  const [mode, setMode] = useState<'pickup' | 'ship'>('pickup');
  const [events, setEvents] = useState<PickupEvent[]>([]);
  const [eventId, setEventId] = useState('');
  const [addr1, setAddr1] = useState(DEV_PREFILL ? '4650 Thomas David Dr' : '');
  const [city, setCity] = useState(DEV_PREFILL ? 'Morrow' : '');
  const [stateCode, setStateCode] = useState(DEV_PREFILL ? 'OH' : '');
  const [zip, setZip] = useState(DEV_PREFILL ? '45152' : '');

  // Live shipping rates (Shippo). When unavailable we fall back to flat $5/$10.
  const [rates, setRates] = useState<RateOption[]>([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState('');
  const [ratesAvailable, setRatesAvailable] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState('');

  const vendorSlug = cart?.vendorSlug || '';
  const vendorName = cart?.vendorName || '';
  const subtotal = cart?.total || 0;
  const addressComplete = !!(addr1.trim() && city.trim() && stateCode.trim() && zip.trim());
  const selectedRate = rates.find((r) => r.rateId === selectedRateId);

  let shipping = 0;
  if (mode === 'ship') {
    shipping = ratesAvailable && selectedRate ? selectedRate.amount : subtotal < 10 ? 5 : 10;
  }
  const grandTotal = subtotal + shipping;

  // Load the vendor's upcoming events so the shopper can choose pickup-at-event.
  useEffect(() => {
    let cancelled = false;
    if (!vendorSlug) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/businesses/${vendorSlug}`);
        if (!res.ok) return;
        const data = await res.json();
        const upcoming: any[] = data?.business?.upcomingEvents || [];
        if (!upcoming.length) {
          if (!cancelled) setMode('ship'); // nothing to pick up at — default to shipping
          return;
        }
        const fairs = await Promise.all(
          upcoming.map(async (u) => {
            try {
              const r = await fetch(`${API_BASE_URL}/api/craft-fairs/${u.craftFairId}`);
              if (!r.ok) return null;
              const f = await r.json();
              return {
                id: f.id,
                name: f.name,
                date: f.date ? new Date(f.date).toISOString().slice(0, 10) : '',
                city: f.location?.city,
                state: f.location?.state,
                venue: f.location?.venue,
              } as PickupEvent;
            } catch {
              return null;
            }
          })
        );
        const valid = (fairs.filter(Boolean) as PickupEvent[]).sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        if (cancelled) return;
        setEvents(valid);
        if (valid.length) setEventId(valid[0].id);
        else setMode('ship');
      } catch {
        /* events are optional; ship path still works */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vendorSlug]);

  useEffect(() => {
    let cancelled = false;
    if (!vendorSlug || done) return;
    (async () => {
      setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/storefront/${vendorSlug}/square-config?env=${CHECKOUT_ENV}`);
        const cfg = await res.json();
        if (cancelled) return;
        if (!cfg.connected || !cfg.application_id || !cfg.location_id) {
          setError('This shop isn’t set up to take card payments yet.');
          return;
        }
        const Square = await loadSquareSdk(cfg.sandbox !== false);
        if (cancelled || !Square) return;
        const payments = Square.payments(cfg.application_id, cfg.location_id);
        const card = await payments.card();
        await card.attach('#square-card');
        if (cancelled) {
          try { await card.destroy(); } catch { /* ignore */ }
          return;
        }
        cardRef.current = card;
        setCardReady(true);
      } catch (e: any) {
        if (!cancelled) setError('Couldn’t load the payment form. Please refresh.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vendorSlug, done]);

  // Fetch live shipping rates when "Ship to me" is chosen and the address is complete.
  useEffect(() => {
    if (mode !== 'ship' || !vendorSlug || !addressComplete || !cart) {
      setRates([]);
      setRatesAvailable(false);
      setSelectedRateId('');
      setRatesError('');
      setRatesLoading(false);
      return;
    }
    let cancelled = false;
    setRatesLoading(true);
    setRatesError('');
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/storefront/${vendorSlug}/shipping-rates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart.items.map((i) => ({ productId: i.id, quantity: i.quantity })),
            address: { line1: addr1, city, state: stateCode, zip },
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setRatesError(body.error || 'Could not load shipping rates.');
          setRatesAvailable(false);
          setRates([]);
          setSelectedRateId('');
        } else if (!body.available) {
          // Shop hasn't set up live rates — fall back to flat shipping silently.
          setRatesAvailable(false);
          setRates([]);
          setSelectedRateId('');
        } else {
          setRatesAvailable(true);
          setRates(body.rates || []);
          setSelectedRateId((body.rates && body.rates[0] && body.rates[0].rateId) || '');
        }
      } catch {
        if (!cancelled) {
          setRatesError('Could not load shipping rates.');
          setRatesAvailable(false);
        }
      } finally {
        if (!cancelled) setRatesLoading(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mode, vendorSlug, addressComplete, addr1, city, stateCode, zip, cart]);

  const pay = useCallback(async () => {
    if (!cart || !cardRef.current) return;

    // Validate fulfillment choice before charging.
    let fulfillment: any;
    if (mode === 'pickup') {
      const ev = events.find((e) => e.id === eventId);
      if (!ev) {
        setError('Please choose an event to pick up from.');
        return;
      }
      fulfillment = { mode: 'pickup', craftFairId: ev.id, eventName: ev.name, eventDate: ev.date };
    } else {
      if (!addressComplete) {
        setError('Please enter your full shipping address.');
        return;
      }
      if (ratesLoading) {
        setError('One moment — still fetching shipping rates.');
        return;
      }
      if (ratesAvailable && !selectedRateId) {
        setError('Please choose a shipping option.');
        return;
      }
      fulfillment = { mode: 'ship', address: { line1: addr1, city, state: stateCode, zip } };
      if (ratesAvailable && selectedRateId) fulfillment.shippingRateId = selectedRateId;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your name.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const tok = await cardRef.current.tokenize();
      if (tok.status !== 'OK') {
        setError((tok.errors || []).map((e: any) => e.message).join(', ') || 'Card was declined.');
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/storefront/${vendorSlug}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          env: CHECKOUT_ENV,
          nonce: tok.token,
          contact: { firstName, lastName, email },
          fulfillment,
          items: cart.items.map((i) => ({ productId: i.id, quantity: i.quantity })),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        setError(body.error || 'Payment could not be completed.');
        setSubmitting(false);
        return;
      }
      setPlacedSlug(cart.vendorSlug);
      setPlacedVendorName(cart.vendorName);
      clearCart();
      setDone(true);
    } catch (e: any) {
      setError(e?.message || 'Payment failed.');
    } finally {
      setSubmitting(false);
    }
  }, [cart, vendorSlug, firstName, lastName, email, mode, events, eventId, addr1, city, stateCode, zip, addressComplete, ratesLoading, ratesAvailable, selectedRateId, clearCart]);

  if (done) {
    const shopPath = placedSlug ? `/${placedSlug}` : '/';
    return (
      <div style={{ maxWidth: 600, margin: '60px auto', padding: 40, textAlign: 'center', background: '#f0fdf4', borderRadius: 12, border: '1px solid #86efac' }}>
        <CheckCircle size={64} style={{ color: '#22c55e', marginBottom: 16 }} />
        <h1 style={{ color: '#166534' }}>Order placed!</h1>
        <p style={{ color: '#15803d' }}>Thank you for your purchase{placedVendorName ? ` from ${placedVendorName}` : ''}.</p>
        <Link to={shopPath} style={{ display: 'inline-block', marginTop: 20, padding: '12px 24px', background: '#2c3e50', color: '#fff', textDecoration: 'none', borderRadius: 8 }}>
          Continue shopping
        </Link>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>Your cart is empty.</p>
        <Link to="/">Go back to shopping</Link>
      </div>
    );
  }

  const payDisabled =
    submitting ||
    !cardReady ||
    (mode === 'ship' && (ratesLoading || (ratesAvailable && !selectedRateId)));

  const optionCardStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 14px',
    border: `2px solid ${active ? '#2c3e50' : '#ddd'}`,
    borderRadius: 8,
    background: active ? '#f8f9fa' : '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    color: active ? '#2c3e50' : '#666',
  });

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
      <Link to="/cart" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#666', textDecoration: 'none', marginBottom: 20, fontSize: '0.9rem' }}>
        <ArrowLeft size={16} /> Back to cart
      </Link>
      <h1 style={{ fontSize: '1.8rem', marginBottom: 8 }}>Checkout</h1>
      {vendorName && <p style={{ color: '#666', marginTop: 0 }}>{vendorName}</p>}

      {/* Customer */}
      <h3 style={{ fontSize: '1rem', margin: '20px 0 10px' }}>Your details</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" style={inputStyle} />
        <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" style={inputStyle} />
      </div>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" style={{ ...inputStyle, marginBottom: 20 }} />

      {/* Fulfillment */}
      <h3 style={{ fontSize: '1rem', margin: '20px 0 10px' }}>How would you like to get your order?</h3>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div
          style={optionCardStyle(mode === 'pickup')}
          onClick={() => events.length && setMode('pickup')}
          title={events.length ? '' : 'No upcoming events'}
        >
          <Store size={18} /> Pick up at event
        </div>
        <div style={optionCardStyle(mode === 'ship')} onClick={() => setMode('ship')}>
          <Truck size={18} /> Ship to me
        </div>
      </div>

      {mode === 'pickup' && (
        events.length ? (
          <div style={{ marginBottom: 20 }}>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} style={inputStyle}>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name} — {fmtEventDate(ev.date)}{ev.city ? ` (${ev.city}${ev.state ? `, ${ev.state}` : ''})` : ''}
                </option>
              ))}
            </select>
            {(() => {
              const ev = events.find((e) => e.id === eventId);
              if (!ev) return null;
              return (
                <div style={{ marginTop: 10, fontSize: '0.9rem', color: '#555', display: 'grid', gap: 4 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Calendar size={15} /> {fmtEventDate(ev.date)}</span>
                  {(ev.venue || ev.city) && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <MapPin size={15} /> {[ev.venue, ev.city, ev.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>Free pickup at the event</span>
                </div>
              );
            })()}
          </div>
        ) : (
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 20 }}>No upcoming events — please choose shipping.</p>
        )
      )}

      {mode === 'ship' && (
        <div style={{ marginBottom: 20, display: 'grid', gap: 12 }}>
          <input value={addr1} onChange={(e) => setAddr1(e.target.value)} placeholder="Street address" style={inputStyle} />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={inputStyle} />
            <input value={stateCode} onChange={(e) => setStateCode(e.target.value)} placeholder="State" style={inputStyle} />
            <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP" style={inputStyle} />
          </div>

          {!addressComplete && (
            <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>Enter your address to see shipping options.</p>
          )}
          {addressComplete && ratesLoading && (
            <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>Finding shipping options…</p>
          )}
          {addressComplete && !ratesLoading && ratesAvailable && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Choose a shipping method</div>
              {rates.map((r) => {
                const active = r.rateId === selectedRateId;
                return (
                  <label
                    key={r.rateId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      border: `2px solid ${active ? '#2c3e50' : '#ddd'}`,
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input type="radio" name="shiprate" checked={active} onChange={() => setSelectedRateId(r.rateId)} />
                      <span>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.provider} {r.service}</span>
                        {r.estimatedDays != null && (
                          <span style={{ display: 'block', fontSize: '0.78rem', color: '#777' }}>
                            Est. {r.estimatedDays} day{r.estimatedDays === 1 ? '' : 's'}
                          </span>
                        )}
                      </span>
                    </span>
                    <span style={{ fontWeight: 700 }}>${r.amount.toFixed(2)}</span>
                  </label>
                );
              })}
            </div>
          )}
          {addressComplete && !ratesLoading && !ratesAvailable && (
            <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
              {ratesError ? `${ratesError} ` : ''}Shipping: ${shipping.toFixed(2)} (flat rate)
            </p>
          )}
        </div>
      )}

      {/* Order summary */}
      <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 20 }}>
        {cart.items.map((i) => (
          <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.95rem' }}>
            <span>{i.title} × {i.quantity}</span>
            <span>${(i.unit_price * i.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 8, fontSize: '0.95rem' }}>
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.95rem' }}>
          <span>{mode === 'ship' ? 'Shipping' : 'Pickup'}</span>
          <span>{shipping > 0 ? `$${shipping.toFixed(2)}` : 'Free'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 8, fontWeight: 700 }}>
          <span>Total</span>
          <span>${grandTotal.toFixed(2)}</span>
        </div>
      </div>

      {/* Payment */}
      <h3 style={{ fontSize: '1rem', margin: '0 0 10px' }}>Payment</h3>
      <div id="square-card" style={{ minHeight: 89, marginBottom: 16 }} />

      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', marginBottom: 16, fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      <button
        onClick={pay}
        disabled={payDisabled}
        style={{
          width: '100%',
          padding: 14,
          background: payDisabled ? '#95a5a6' : '#2c3e50',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: payDisabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <Lock size={18} /> {submitting ? 'Processing…' : `Pay $${grandTotal.toFixed(2)}`}
      </button>
      <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#999', marginTop: 12 }}>Payments processed securely by Square</p>
    </div>
  );
}
